import { ForwardedRef, forwardRef, useMemo } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { CheckboxV2 } from './checkbox-v2';
import { CheckboxRhfProps } from './checkbox-v2.types';

type FieldProps = {
  field: ControllerRenderProps<FieldValues, string>;
  fieldState: ControllerFieldState;
};

export const CheckboxRhf = forwardRef(
  (props: CheckboxRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { rules, ...checkboxProps } = props;
    const { control } = useFormContext();
    const renderField = useMemo(
      () => fieldFactory(checkboxProps, ref),
      [checkboxProps, ref],
    );

    return (
      <Controller
        name={props.name}
        control={control}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        render={renderField}
      />
    );
  },
);

// Extracted so the render prop can call `useMergeRefs`.
const fieldFactory = (
  checkboxProps: Omit<CheckboxRhfProps, 'rules'>,
  ref?: ForwardedRef<HTMLInputElement>,
) => {
  return ({ field, fieldState }: FieldProps) => {
    const mergedRefs = useMergeRefs([field.ref, ref]);

    return (
      <CheckboxV2
        {...checkboxProps}
        ref={mergedRefs}
        checked={!!field.value}
        invalid={!!fieldState.error}
        onChange={(e) => {
          field.onChange(e.target.checked);
          checkboxProps.onChange?.(e);
        }}
        onBlur={(e) => {
          field.onBlur();
          checkboxProps.onBlur?.(e);
        }}
      />
    );
  };
};
